/**
 * Survey rules shared by the API, the worker and the web (ADR 0027). Pure and RN-safe.
 *
 * One implementation each of "is this survey open on this day", "does this answer fit this
 * question", "is this survey complete enough to publish" and "what does this question's tally look
 * like", so the employee form, the API and the results dashboard cannot disagree.
 */

import type {
  SurveyAnswerInput,
  SurveyMatrix,
  SurveyOption,
  SurveyQuestionType,
  SurveyStatus,
} from './contracts/survey.contract.js';

export interface SurveyTimingFacts {
  /** `draft` | `published`. */
  readonly status: string;
  /** `YYYY-MM-DD`. */
  readonly opensOn: string;
  readonly closesOn: string | null;
  readonly closedAt: Date | string | null;
}

/** Derived status on `day` (`YYYY-MM-DD`, the viewer's location day). Close date is inclusive. */
export function surveyStatusOn(survey: SurveyTimingFacts, day: string): SurveyStatus {
  if (survey.status !== 'published') return 'draft';
  if (survey.closedAt) return 'closed';
  if (day < survey.opensOn) return 'scheduled';
  if (survey.closesOn !== null && day > survey.closesOn) return 'closed';
  return 'open';
}

// ---------------------------------------------------------------------------
// Question types
// ---------------------------------------------------------------------------

/** Yes/no has two fixed options; the server stores these, so every choice rule reads one list. */
export const YES_NO_OPTIONS: readonly SurveyOption[] = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];

/** Types answered with one option from a list (a radio group, a drop-down or yes/no). */
export const SINGLE_CHOICE_TYPES: readonly SurveyQuestionType[] = [
  'single_choice',
  'dropdown',
  'yes_no',
];

/** Types that carry an option list in `options`: single and multiple choice, drop-down, yes/no. */
export const CHOICE_TYPES: readonly SurveyQuestionType[] = [
  'single_choice',
  'multiple_choice',
  'dropdown',
  'yes_no',
];

/** 1-5 scales, stored in `numberValue`. */
const RANGE: Partial<Record<SurveyQuestionType, readonly [number, number]>> = {
  rating: [1, 5],
  opinion_scale: [1, 5],
  enps: [0, 10],
};

/** Free text, stored in `textValue`. */
const TEXT_TYPES: readonly SurveyQuestionType[] = ['text', 'long_text'];

/** Separates a matrix row id from its column id in a stored answer (`rowId:columnId`). */
export const MATRIX_SEPARATOR = ':';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Whether `value` is a real calendar date written `YYYY-MM-DD`. */
export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
}

/** An opinion scale's two end labels are stored in `options` under these ids. */
export const SCALE_LABEL_IDS = { low: 'low', high: 'high' } as const;

/** The labels of an opinion scale's ends, with the defaults when none were given. */
export function opinionScaleLabels(options: readonly SurveyOption[]): { low: string; high: string } {
  const label = (id: string, fallback: string) =>
    options.find((o) => o.id === id)?.label || fallback;
  return {
    low: label(SCALE_LABEL_IDS.low, 'Strongly disagree'),
    high: label(SCALE_LABEL_IDS.high, 'Strongly agree'),
  };
}

/** The option list a question is answered from: fixed for yes/no, otherwise as stored. */
export function optionsFor(question: { type: SurveyQuestionType; options: readonly SurveyOption[] }) {
  return question.type === 'yes_no' ? YES_NO_OPTIONS : question.options;
}

export interface SurveyQuestionFacts {
  readonly id: string;
  readonly type: SurveyQuestionType;
  readonly required: boolean;
  readonly options: readonly SurveyOption[];
  /** Matrix only. */
  readonly matrix?: SurveyMatrix | null;
}

/** A validated answer, reduced to the fields its question type reads. `optionIds` for a matrix
 *  holds one `rowId:columnId` per answered row. */
export interface NormalisedSurveyAnswer {
  readonly questionId: string;
  readonly numberValue: number | null;
  readonly optionIds: string[];
  readonly textValue: string | null;
}

/**
 * Checks a whole submission against the questions. Returns the answers to store (unanswered
 * optional questions dropped) or the problems, one line per question, for a 422.
 */
export function validateSurveyAnswers(
  questions: readonly SurveyQuestionFacts[],
  answers: readonly SurveyAnswerInput[],
): { ok: true; answers: NormalisedSurveyAnswer[] } | { ok: false; problems: string[] } {
  const problems: string[] = [];
  const byQuestion = new Map<string, SurveyAnswerInput>();
  for (const a of answers) {
    if (byQuestion.has(a.questionId)) problems.push(`Question ${a.questionId} is answered twice`);
    byQuestion.set(a.questionId, a);
  }
  const known = new Set(questions.map((q) => q.id));
  for (const id of byQuestion.keys()) {
    if (!known.has(id)) problems.push(`Question ${id} is not part of this survey`);
  }

  const out: NormalisedSurveyAnswer[] = [];
  questions.forEach((q, index) => {
    const label = `Question ${index + 1}`;
    const a = byQuestion.get(q.id);
    const range = RANGE[q.type];
    let answer: NormalisedSurveyAnswer | null = null;

    if (range) {
      if (a?.number !== undefined) {
        if (a.number < range[0] || a.number > range[1]) {
          problems.push(`${label} takes a value from ${range[0]} to ${range[1]}`);
          return;
        }
        answer = { questionId: q.id, numberValue: a.number, optionIds: [], textValue: null };
      }
    } else if (TEXT_TYPES.includes(q.type)) {
      const text = a?.text?.trim();
      if (text) answer = { questionId: q.id, numberValue: null, optionIds: [], textValue: text };
    } else if (q.type === 'date') {
      if (a?.date !== undefined) {
        if (!isIsoDate(a.date)) {
          problems.push(`${label} takes a real date`);
          return;
        }
        answer = { questionId: q.id, numberValue: null, optionIds: [], textValue: a.date };
      }
    } else if (q.type === 'matrix') {
      const matrixAnswer = validateMatrixAnswer(q, label, a?.matrix ?? []);
      if (!matrixAnswer.ok) {
        problems.push(...matrixAnswer.problems);
        return;
      }
      const given = matrixAnswer.optionIds.length;
      if (q.required && given === 0) {
        problems.push(`${label} is required`);
        return;
      }
      if (q.required && given < (q.matrix?.rows.length ?? 0)) {
        problems.push(`${label} needs an answer for every row`);
        return;
      }
      if (given > 0) {
        answer = { questionId: q.id, numberValue: null, optionIds: matrixAnswer.optionIds, textValue: null };
      }
    } else {
      const ids = [...new Set(a?.optionIds ?? [])];
      if (ids.length > 0) {
        const valid = new Set(optionsFor(q).map((o) => o.id));
        if (ids.some((id) => !valid.has(id))) {
          problems.push(`${label} names an option it does not have`);
          return;
        }
        if (SINGLE_CHOICE_TYPES.includes(q.type) && ids.length > 1) {
          problems.push(`${label} takes one choice`);
          return;
        }
        answer = { questionId: q.id, numberValue: null, optionIds: ids, textValue: null };
      }
    }

    if (answer) out.push(answer);
    else if (q.required) problems.push(`${label} is required`);
  });

  return problems.length > 0 ? { ok: false, problems } : { ok: true, answers: out };
}

/** One `rowId:columnId` per row answered, after checking each row and column exists, once. */
function validateMatrixAnswer(
  q: SurveyQuestionFacts,
  label: string,
  given: readonly { rowId: string; columnId: string }[],
): { ok: true; optionIds: string[] } | { ok: false; problems: string[] } {
  const rows = new Set((q.matrix?.rows ?? []).map((r) => r.id));
  const columns = new Set((q.matrix?.columns ?? []).map((c) => c.id));
  const problems: string[] = [];
  const seen = new Set<string>();
  const optionIds: string[] = [];
  for (const { rowId, columnId } of given) {
    if (!rows.has(rowId)) {
      problems.push(`${label} names a row it does not have`);
      continue;
    }
    if (!columns.has(columnId)) {
      problems.push(`${label} names a column it does not have`);
      continue;
    }
    if (seen.has(rowId)) {
      problems.push(`${label} answers a row twice`);
      continue;
    }
    seen.add(rowId);
    optionIds.push(`${rowId}${MATRIX_SEPARATOR}${columnId}`);
  }
  return problems.length > 0 ? { ok: false, problems } : { ok: true, optionIds };
}

export interface SurveyPublishFacts {
  readonly kind: 'survey' | 'poll';
  readonly opensOn: string;
  readonly closesOn: string | null;
  readonly questions: readonly {
    type: SurveyQuestionType;
    options: readonly SurveyOption[];
    matrix?: SurveyMatrix | null;
  }[];
}

/** Why a draft cannot be published yet; empty when it can. */
export function surveyPublishProblems(s: SurveyPublishFacts): string[] {
  const problems: string[] = [];
  if (s.questions.length === 0) problems.push('Add at least one question');
  if (s.kind === 'poll') {
    const [only] = s.questions;
    if (
      s.questions.length !== 1 ||
      (only && only.type !== 'single_choice' && only.type !== 'multiple_choice')
    ) {
      problems.push('A poll has exactly one single- or multiple-choice question');
    }
  } else if (s.closesOn === null) {
    problems.push('A survey needs a close date');
  }
  if (s.closesOn !== null && s.closesOn < s.opensOn) {
    problems.push('The close date is before the open date');
  }
  s.questions.forEach((q, i) => {
    const label = `Question ${i + 1}`;
    const options = optionsFor(q);
    if (CHOICE_TYPES.includes(q.type) && q.type !== 'yes_no') {
      if (options.length < 2) problems.push(`${label} needs at least two options`);
      if (new Set(options.map((o) => o.id)).size !== options.length) {
        problems.push(`${label} has two options with the same id`);
      }
    }
    if (q.type === 'matrix') {
      const rows = q.matrix?.rows ?? [];
      const columns = q.matrix?.columns ?? [];
      if (rows.length === 0) problems.push(`${label} needs at least one row`);
      if (columns.length < 2) problems.push(`${label} needs at least two columns`);
      if (new Set(rows.map((r) => r.id)).size !== rows.length) {
        problems.push(`${label} has two rows with the same id`);
      }
      if (new Set(columns.map((c) => c.id)).size !== columns.length) {
        problems.push(`${label} has two columns with the same id`);
      }
    }
  });
  return problems;
}

// ---------------------------------------------------------------------------
// Tallies
// ---------------------------------------------------------------------------

export interface SurveyTally {
  readonly answered: number;
  readonly distribution: { value: string; label: string; count: number }[];
  readonly average: number | null;
  readonly enps: { score: number; promoters: number; passives: number; detractors: number } | null;
  /** Date only: every answer, sorted ascending. */
  readonly dates: string[];
  /** Matrix only: one entry per row; `answered` counts the answers to that row. */
  readonly matrixRows: {
    rowId: string;
    label: string;
    answered: number;
    distribution: { value: string; label: string; count: number }[];
  }[];
}

/** eNPS: % promoters (9-10) minus % detractors (0-6), rounded; null with no answers. */
export function enpsOf(values: readonly number[]) {
  if (values.length === 0) return null;
  const promoters = values.filter((v) => v >= 9).length;
  const detractors = values.filter((v) => v <= 6).length;
  const passives = values.length - promoters - detractors;
  const score = Math.round(((promoters - detractors) / values.length) * 100);
  return { score: score === 0 ? 0 : score, promoters, passives, detractors };
}

/** The answer fields a tally reads. For a date, `textValue` holds the date. */
export interface SurveyTallyAnswer {
  readonly numberValue: number | null;
  readonly optionIds: readonly string[];
  readonly textValue?: string | null;
}

/** One question's distribution over the answers given to it (free text has none). */
export function tallySurveyQuestion(
  question: {
    type: SurveyQuestionType;
    options: readonly SurveyOption[];
    matrix?: SurveyMatrix | null;
  },
  answers: readonly SurveyTallyAnswer[],
): SurveyTally {
  const empty: Omit<SurveyTally, 'answered' | 'distribution'> = {
    average: null,
    enps: null,
    dates: [],
    matrixRows: [],
  };
  const range = RANGE[question.type];
  if (range) {
    const values = answers.map((a) => a.numberValue).filter((v): v is number => v !== null);
    const distribution = [];
    for (let v = range[0]; v <= range[1]; v += 1) {
      distribution.push({
        value: String(v),
        label: String(v),
        count: values.filter((x) => x === v).length,
      });
    }
    const average =
      (question.type === 'rating' || question.type === 'opinion_scale') && values.length > 0
        ? Math.round((values.reduce((s, v) => s + v, 0) / values.length) * 100) / 100
        : null;
    return {
      ...empty,
      answered: values.length,
      distribution,
      average,
      enps: question.type === 'enps' ? enpsOf(values) : null,
    };
  }
  if (TEXT_TYPES.includes(question.type)) {
    return { ...empty, answered: answers.length, distribution: [] };
  }
  if (question.type === 'date') {
    const dates = answers
      .map((a) => a.textValue)
      .filter((v): v is string => typeof v === 'string' && v !== '')
      .sort();
    return { ...empty, answered: dates.length, distribution: [], dates };
  }
  if (question.type === 'matrix') {
    const rows = question.matrix?.rows ?? [];
    const columns = question.matrix?.columns ?? [];
    const answered = answers.filter((a) => a.optionIds.length > 0);
    const matrixRows = rows.map((row) => {
      const prefix = `${row.id}${MATRIX_SEPARATOR}`;
      const forRow = answered.filter((a) => a.optionIds.some((id) => id.startsWith(prefix)));
      return {
        rowId: row.id,
        label: row.label,
        answered: forRow.length,
        distribution: columns.map((c) => ({
          value: c.id,
          label: c.label,
          count: forRow.filter((a) => a.optionIds.includes(`${prefix}${c.id}`)).length,
        })),
      };
    });
    return { ...empty, answered: answered.length, distribution: [], matrixRows };
  }
  const withChoice = answers.filter((a) => a.optionIds.length > 0);
  return {
    ...empty,
    answered: withChoice.length,
    distribution: optionsFor(question).map((o) => ({
      value: o.id,
      label: o.label,
      count: withChoice.filter((a) => a.optionIds.includes(o.id)).length,
    })),
  };
}

/**
 * Whether one slice of an anonymous survey may be shown. Every released number must cover at least
 * `min` responses AND leave at least `min` responses for the rest, or subtraction exposes the rest.
 * `isWhole` (the "Everyone" slice) has no rest to protect, so only its own size is checked.
 */
export function sliceReleasable(input: {
  count: number;
  total: number;
  min: number;
  anonymous: boolean;
  isWhole: boolean;
}): boolean {
  if (!input.anonymous) return true;
  if (input.count < input.min) return false;
  if (input.isWhole) return true;
  return input.total - input.count >= input.min;
}

/**
 * Anonymous results are released only once the survey has closed. Live counts let someone compare
 * two snapshots and isolate the one answer that changed, so nothing is shown while it is open.
 */
export function anonymousResultsReleased(input: { anonymous: boolean; status: SurveyStatus }): boolean {
  return !input.anonymous || input.status === 'closed';
}
